import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTestimonials } from "../hooks/useTestimonials";
import { analytics, EVENTS } from "../analytics";

interface I18nTestimonial {
  header: string;
  content: string;
  star: number;
  experience: string;
  isHTML?: boolean;
}

const HIGHLIGHT_INDICES = [0, 1, 7];

const StarRow: React.FC<{ score: number }> = ({ score }) => {
  const stars = Math.round(score);
  return (
    <div className="flex gap-0.5 mb-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg
          key={i}
          viewBox="0 0 20 20"
          className={`w-3.5 h-3.5 ${i < stars ? "fill-accent text-accent" : "fill-gray-200 text-gray-200"}`}
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

const HomeTestimonialsHighlight: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { testimonials, loading } = useTestimonials();

  const { title, viewAll } = t("testimonialsHighlight", { returnObjects: true }) as {
    title: string;
    viewAll: string;
  };

  // i18n fallback data
  const i18nTestimonials = (
    t("testimonialsSection", { returnObjects: true }) as { testimonials: I18nTestimonial[] }
  ).testimonials;

  // Use DB data if available, otherwise fall back to i18n
  const useDB = !loading && testimonials.length > 0;
  const dbHighlights = testimonials.slice(0, 3);
  const i18nHighlights = HIGHLIGHT_INDICES.map((i) => i18nTestimonials[i]).filter(Boolean);

  // Still loading — render nothing (avoids layout shift)
  if (loading) return null;

  return (
    <section className="py-20 bg-sub">
      <div className="max-w-6xl mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-main text-center mb-12">
          {title}
        </h2>

        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {useDB
            ? dbHighlights.map((item, i) => {
                const preview =
                  item.content.length > 160
                    ? item.content.slice(0, 160).trimEnd() + "..."
                    : item.content;
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col"
                  >
                    <StarRow score={item.quality_score !== null ? item.quality_score / 2 : 5} />
                    {item.header && <p className="font-semibold text-[#0A1A3E] text-sm mt-2 mb-1">{item.header}</p>}
                    <p className="text-gray-600 text-xs leading-relaxed flex-1">{preview}</p>
                    <p className="mt-4 text-xs text-gray-400 border-t border-gray-100 pt-3">
                      {item.experience ?? item.title ?? ""}
                    </p>
                  </motion.div>
                );
              })
            : i18nHighlights.map((item, i) => {
                const plainText = item.isHTML
                  ? item.content.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim()
                  : item.content;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col"
                  >
                    <StarRow score={item.star} />
                    <p className="font-semibold text-main text-sm mb-3 leading-snug">
                      {item.header}
                    </p>
                    <p className="text-gray-500 text-xs leading-relaxed flex-1 line-clamp-4">
                      {plainText}
                    </p>
                    <p className="mt-4 text-xs text-gray-400 border-t border-gray-100 pt-3">
                      {item.experience}
                    </p>
                  </motion.div>
                );
              })}
        </div>

        <div className="text-center">
          <button
            onClick={() => {
              navigate("/about/testimonials");
              window.scrollTo({ top: 0, behavior: "smooth" });
              analytics.track(EVENTS.CLICK_HOME_TESTIMONIALS_VIEW_ALL);
            }}
            className="text-main font-semibold text-sm hover:underline underline-offset-4"
          >
            {viewAll} →
          </button>
        </div>
      </div>
    </section>
  );
};

export default HomeTestimonialsHighlight;
